import { put, takeLatest, call, all, delay } from 'redux-saga/effects';
import { select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';
import { format, startOfMonth, subMonths } from 'date-fns';

import { handleErrorSaga, handleWarningSaga } from 'src/utils/handleErrorSaga';
import { handleSuccessSaga } from 'src/utils/handleSuccessSaga';
import { calculateStatusTimes } from 'src/components/LogView/utils';
import { startLoading, stopLoading } from 'src/features/loading/loadingStore';

import {
  FetchLogsPayload,
  FetchLogPayload,
  ResponseListData,
  CertifyHosLogPayload,
  SaveStatusPayload,
  PendingException,
  SaveExceptionPayload,
  MultiplyCertifyHosLogs,
  ExportAllHosLogsPayload,
  SaveFormsPayload,
  FetchHosExportLinkPayload,
  FetchHosExportLinkResponse,
} from 'src/common/types';
import {
  ApproveRejectPendingEditsType,
  PendingEditsListType,
  HosDailyInfo,
} from 'src/features/hosLogs/hosLog/types';

import {
  fetchHosLogsApi,
  fetchHosLogApi,
  editLogStatus,
  saveException,
  removeException,
  fetchUserLocations,
  insertDutyStatus,
  fetchPendingEditsApi,
  patchPendingEditsApi,
  exportAllHosLogsApi,
  saveFormsApi,
  fetchDailyHeaderApi,
  fetchHosExportLinkApi,
} from 'src/api/hosApi';
import { certifyHosLog, multiplyCertifyHosLogs } from 'src/api/hosLogCertifyApi';

import {
  fetchHosLogs,
  setHosLogs,
  fetchHosLog,
  setHosLog,
  setTimelineData,
  certifyHosLogAction,
  editLogStatusAction,
  saveExceptionLog,
  fetchUserLocationsAction,
  setUserLocationData,
  clearEditState,
  setEditingStatus,
  insertDutyStatusAction,
  multiplyCertifyHosLogsAction,
  setPendingEditsList,
  setPendingEditsTimelineData,
  approveRejectPendingEditsAction,
  setCurrentDayHosLog,
  fetchCurrentDayHosLog,
  resetPendingEditsTimelineData,
  exportAllHosLogsAction,
  fetchCalendarHosLogsAction,
  fetchHosExportLinkAction,
  saveLogFormsAction,
  setCalendarHosLogs,
  setInsertingDutyStatus,
  setHosDaily,
  setHosExportJobId,
  setHosExportLink,
  resetHosDaily,
} from './hosLogStore';

import { normalizeHosGridObject } from './utils';
import { getPendingExceptions, getPendingRemovalExceptions } from './hosLogSelectors';
import { HosLogObject } from '../types';
import { fetchCalendarDotsLogsCount } from 'src/configs/gui-config';
import { AxiosError } from 'axios';

const fetchCalendarLogsPayload = (logId: number): FetchLogsPayload => ({
  startDate: format(startOfMonth(subMonths(new Date(), 6)), 'yyyy-MM-dd'),
  endDate: format(new Date(), 'yyyy-MM-dd'),
  employeeIds: logId,
  count: fetchCalendarDotsLogsCount,
  startIndex: 0,
});

function* fetchHosLogsSaga(action: PayloadAction<FetchLogsPayload>): SagaIterator {
  try {
    yield put(startLoading());
    const hosLogsResponse: ResponseListData = yield call(fetchHosLogsApi, action.payload);
    const hosLogs: ResponseListData = {
      ...hosLogsResponse,
      results: hosLogsResponse.results.map(log => {
        const {
          hosGridObject,
          logStartTimeISO,
          logEndTimeISO,
          logDayStartTime,
          logLengthHours,
        } = log;
        const timelineData = {
          hosGridObject,
          statusTimes: calculateStatusTimes({
            logDate: log.date,
            log,
          }),
          logStartTimeISO,
          logEndTimeISO,
          logDayStartTime,
          logLengthHours,
        };
        return {
          ...log,
          timelineData,
        };
      }),
    };
    yield put(setHosLogs(hosLogs));
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* fetchHosLogSaga(action: PayloadAction<FetchLogPayload>): SagaIterator {
  try {
    yield put(startLoading());

    const [hosLogData, pendingEdits, hosDaily]: [
      HosLogObject,
      PendingEditsListType[],
      HosDailyInfo,
    ] = yield all([
      call(fetchHosLogApi, action.payload),
      call(fetchPendingEditsApi, action.payload),
      call(fetchDailyHeaderApi, action.payload),
    ]);

    // hos log
    const hosGridObject = normalizeHosGridObject(hosLogData.hosGridObject);
    const { logStartTimeISO, logEndTimeISO, logDayStartTime, logLengthHours } = hosLogData;
    const violationsObject = hosLogData.violations;
    const timelineData = {
      hosGridObject,
      logStartTimeISO,
      logEndTimeISO,
      logDayStartTime,
      logLengthHours,
      statusTimes: calculateStatusTimes({
        logDate: hosLogData.fmcsaLogDate,
        log: {
          ...hosLogData,
          hosGridObject,
        },
      }),
      violationsObject,
    };
    yield put(setTimelineData(timelineData));
    yield put(
      setHosLog({
        ...hosLogData,
        hosGridObject,
      }),
    );

    // pending edits
    yield put(setPendingEditsList(pendingEdits));
    if (Array.isArray(pendingEdits) && pendingEdits.length > 0) {
      const pendingEditsHosGridObject = normalizeHosGridObject(pendingEdits[0].hosGridObject);
      const pendingEditsTimelineData = {
        hosGridObject: pendingEditsHosGridObject,
        statusTimes: calculateStatusTimes({
          logDate: pendingEdits[0].logDate,
          log: {
            ...hosLogData,
            hosGridObject: pendingEditsHosGridObject,
          },
        }),
        violationsObject: [],
      };
      yield put(setPendingEditsTimelineData(pendingEditsTimelineData));
    } else {
      yield put(resetPendingEditsTimelineData());
    }

    yield put(setHosDaily(hosDaily));
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError?.response?.data) {
      const errorMessage = axiosError.response.data.error;
      if (errorMessage.includes('LOG_DOES_NOT_EXIST')) {
        const cause = errorMessage.split('-')[1].trim();
        yield put(resetHosDaily());
        yield call(handleWarningSaga, cause);
      }
    } else {
      yield call(handleErrorSaga, error);
    }
  } finally {
    yield put(stopLoading());
  }
}

function* certifyHosLogSaga(action: PayloadAction<CertifyHosLogPayload>): SagaIterator {
  try {
    yield put(startLoading());
    yield call(certifyHosLog, action.payload);
    yield call(handleSuccessSaga, 'Log successfully certificated');
    const logId = `${action.payload.logId}:${action.payload.logDate}`;
    yield put(fetchHosLog({ logId }));
    const hosLogsResponse: ResponseListData = yield call(
      fetchHosLogsApi,
      fetchCalendarLogsPayload(action.payload.logId),
    );
    yield put(setCalendarHosLogs(hosLogsResponse));
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* multiplyCertifyHosLogsSaga(action: PayloadAction<MultiplyCertifyHosLogs>): SagaIterator {
  try {
    yield put(startLoading());
    yield call(multiplyCertifyHosLogs, action.payload.logIds);
    yield call(handleSuccessSaga, 'Your logs successfully certificated');
    if (action.payload.updateDay) {
      const logId = `${action.payload.employeeId}:${action.payload.logDate}`;
      yield put(fetchHosLog({ logId }));
    }
    const hosLogsResponse: ResponseListData = yield call(
      fetchHosLogsApi,
      fetchCalendarLogsPayload(action.payload.employeeId),
    );
    yield put(setCalendarHosLogs(hosLogsResponse));
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* editLogStatusSaga(action: PayloadAction<SaveStatusPayload>): SagaIterator {
  try {
    yield put(startLoading());
    yield call(editLogStatus, action.payload);
    yield call(handleSuccessSaga, 'Your log successfully edited');
    yield put(setEditingStatus(undefined));
    const logId = `${action.payload.employeeId}:${action.payload.logDate}`;
    yield put(fetchHosLog({ logId }));
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* saveExceptionSaga(action: PayloadAction<SaveExceptionPayload>): SagaIterator {
  try {
    const { employeeId, exceptionDate } = action.payload;
    yield put(startLoading());
    const pendingException: PendingException[] = yield select(getPendingExceptions);
    yield all(pendingException.map(item => call(saveException, item)));
    const pendingRemovalExceptions: PendingException[] = yield select(getPendingRemovalExceptions);
    yield all(pendingRemovalExceptions.map(item => call(removeException, item)));
    yield put(clearEditState());
    yield put(fetchHosLog({ logId: `${employeeId}:${exceptionDate}` }));
    yield call(handleSuccessSaga, 'You successfully saved edits');
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* insertDutyStatusSaga(action: PayloadAction<SaveStatusPayload>): SagaIterator {
  try {
    yield put(startLoading());
    yield call(insertDutyStatus, action.payload);
    yield call(handleSuccessSaga, 'You successfully inserted status');
    const logId = `${action.payload.employeeId}:${action.payload.logDate}`;
    yield put(fetchHosLog({ logId }));
    yield delay(1000);
    yield put(setInsertingDutyStatus(false));
    yield put(stopLoading());
  } catch (error) {
    yield call(handleErrorSaga, error);
    yield put(stopLoading());
  }
}

function* fetchUserLocationSaga(action: PayloadAction<string>): SagaIterator {
  try {
    yield put(startLoading());
    const userLocationResponse = yield call(fetchUserLocations, action.payload);
    yield put(setUserLocationData(userLocationResponse));
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* approveRejectPendingEditsSaga(
  action: PayloadAction<ApproveRejectPendingEditsType>,
): SagaIterator {
  try {
    yield put(startLoading());
    yield call(patchPendingEditsApi, action.payload);
    yield call(handleSuccessSaga, 'Successfully updated');
    yield put(fetchHosLog({ logId: action.payload.logId }));
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* fetchCurrentDayHosLogSaga(action: PayloadAction<FetchLogPayload>): SagaIterator {
  try {
    yield put(startLoading());
    const hosLogData = yield call(fetchHosLogApi, action.payload);
    const hosGridObject = normalizeHosGridObject(hosLogData.hosGridObject);
    yield put(
      setCurrentDayHosLog({
        ...hosLogData,
        hosGridObject,
      }),
    );
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* exportAllHosLogsSaga(action: PayloadAction<ExportAllHosLogsPayload>): SagaIterator {
  try {
    const hosExportResponse: FetchHosExportLinkPayload = yield call(
      exportAllHosLogsApi,
      action.payload,
    );
    yield put(setHosExportJobId(hosExportResponse));
  } catch (error) {
    yield call(handleErrorSaga, error);
  }
}

function* fetchHosExportLinkSaga(action: PayloadAction<FetchHosExportLinkPayload>): SagaIterator {
  try {
    const hosExportLinkResponse: FetchHosExportLinkResponse = yield call(
      fetchHosExportLinkApi,
      action.payload,
    );
    yield put(setHosExportLink(hosExportLinkResponse));
  } catch (error) {
    yield call(handleErrorSaga, error);
  }
}

function* fetchCalendarHosLogsSaga(action: PayloadAction<FetchLogsPayload>): SagaIterator {
  try {
    yield put(startLoading());
    const hosLogsResponse: ResponseListData = yield call(fetchHosLogsApi, action.payload);
    yield put(setCalendarHosLogs(hosLogsResponse));
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

function* saveLogFormsSaga(action: PayloadAction<SaveFormsPayload>): SagaIterator {
  try {
    yield put(startLoading());
    yield call(saveFormsApi, action.payload);
    yield call(handleSuccessSaga, 'Request successfully sent');
  } catch (error) {
    yield call(handleErrorSaga, error);
  } finally {
    yield put(stopLoading());
  }
}

export function* watchHosLogsSagas(): SagaIterator {
  yield takeLatest(fetchHosLogs, fetchHosLogsSaga);
  yield takeLatest(fetchHosLog, fetchHosLogSaga);
  yield takeLatest(certifyHosLogAction, certifyHosLogSaga);
  yield takeLatest(multiplyCertifyHosLogsAction, multiplyCertifyHosLogsSaga);
  yield takeLatest(editLogStatusAction, editLogStatusSaga);
  yield takeLatest(saveExceptionLog, saveExceptionSaga);
  yield takeLatest(fetchUserLocationsAction, fetchUserLocationSaga);
  yield takeLatest(insertDutyStatusAction, insertDutyStatusSaga);
  yield takeLatest(approveRejectPendingEditsAction, approveRejectPendingEditsSaga);
  yield takeLatest(fetchCurrentDayHosLog, fetchCurrentDayHosLogSaga);
  yield takeLatest(exportAllHosLogsAction, exportAllHosLogsSaga);
  yield takeLatest(fetchHosExportLinkAction, fetchHosExportLinkSaga);
  yield takeLatest(fetchCalendarHosLogsAction, fetchCalendarHosLogsSaga);
  yield takeLatest(saveLogFormsAction, saveLogFormsSaga);
}
